import { Icon, MenuItem, Tooltip } from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import _ from "lodash";
import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";

import { GetSlidesMetadataResponse } from "apis/presentations/types";
import { ActionPermissionsObject, PermissionActionType } from "common/interfaces";
import UpgradeIcon from "js/Components/UpgradeIcon";
import PresentationTemplatesPaneController, { PresentationTemplatesPaneControllerState } from "js/controllers/PresentationTemplatesPaneController";
import WorkspaceController from "js/controllers/WorkspaceController";
import AppController from "js/core/AppController";
import getLogger, { LogGroup } from "js/core/logger";
import getUserProfile from "js/core/services/userProfiles";
import withProgressDialog from "js/core/utilities/withProgressDialog";
import { t } from "js/i18n";
import { ShowConfirmationDialog, ShowDialog, ShowErrorDialog } from "js/react/components/Dialogs/BaseDialog";
import { FlexSpacer, Gap10, Gap20, Gap5 } from "js/react/components/Gap";
import { FlexBox } from "js/react/components/LayoutGrid";
import { IconMenu } from "js/react/components/UiComponents";
import { useIntersectionObserver } from "js/react/hooks/useIntersectionObserver";
import { themeColors } from "js/react/sharedStyles";
import { EmptySlideThumbnail, SlideThumbnail } from "js/react/views/AddSlide/Panes/Components/ThumbnailGrid";
import TeamTemplateSettingsDialog from "js/react/views/TeamResources/dialogs/TeamTemplateSettingsDialog";
import { useFirebaseUser } from "js/react/hooks/useUser";

const logger = getLogger(LogGroup.WORKSPACES);

const StyledTemplateItem = styled.div`
    width: 100%;
    margin-bottom: 20px;
    background: white;
    padding: 10px 20px 10px 20px;
    border: solid 1px white;

    &:hover {
        border: solid 1px ${themeColors.ui_blue};
    }

    &.selected {
        background: ${themeColors.lightBlue};
        border: solid 1px ${themeColors.lightBlue};

        &:hover {
            border: solid 1px ${themeColors.ui_blue};
        }
    }

    .title {
        font-weight: 600;
        font-size: 16px;
    }
`;

const SlideThumbnailsContainer = styled.div`
    width: 100%;
    height: 142px;
`;

const SlideThumbnails = styled.div`
    display: grid;
    grid-auto-flow: column;
    grid-auto-columns: 200px;
    width: 100%;
    overflow-x: auto;
    gap: 10px;
    padding: 10px 1px;
`;

const PublishLabel = styled.div<{ published?: boolean }>`
    padding: 5px 10px;
    color: white;
    background: ${props => props.published ? themeColors.ui_blue : "orangered"};
    border-radius: 2px;
    text-transform: uppercase;
    font-size: 12px;
`;

const Stat = styled.div<{ active?: boolean, liked?: boolean }>`
    display: flex;
    align-items: center;
    color: rgba(102, 102, 102, ${props => props.active ? 1 : 0.5});

    &.ratedBy {
        cursor: pointer;

        .MuiIcon-root {
            color: ${props => props.liked ? themeColors.yellow : "inherit"};
        }
    }

    &:hover {
        color: ${themeColors.ui_blue};

        .MuiIcon-root {
            color: ${themeColors.ui_blue};
        }
    }
`;

const StyledCreatedByInfo = styled.div`
    padding: 10px;
`;

export interface PresentationTemplateItemProps extends PresentationTemplatesPaneControllerState {
    record: PresentationTemplatesPaneControllerState["data"][number];
    editable: boolean;
    onVisibleChange: (isVisible: boolean) => void;
    onSelect: (presentationId: string | null, isDoubleClick?: boolean) => void;
    selected: boolean;
    getPresentationSlidesMetadata: (presentationId: string) => Promise<GetSlidesMetadataResponse>;
}

export const PresentationTemplateItem = PresentationTemplatesPaneController.withState(function PresentationTemplateItem(props: PresentationTemplateItemProps) {
    const {
        record,
        editable,
        onSelect,
        selected,
        tags,
        getPresentationSlidesMetadata,
        onVisibleChange
    } = props;

    const { firebaseUser } = useFirebaseUser();
    const [slidesMetadata, setSlidesMetadata] = useState<GetSlidesMetadataResponse>({});
    const [createdByName, setCreatedByName] = useState("");

    const containerRef = useRef<HTMLDivElement>(null);

    const isIntersecting = useIntersectionObserver(containerRef);

    useEffect(() => {
        onVisibleChange(isIntersecting);
    }, [isIntersecting]);

    const presentation = record.document;
    const resource = record.resource;

    const slideIds: string[] = new Array(Object.keys(presentation.slideRefs).length);
    Object.entries(presentation.slideRefs).forEach(([slideId, idx]) => {
        slideIds[idx] = slideId;
    });

    useEffect(() => {
        (async () => {
            try {
                const [profile, slidesMetadata] = await Promise.all([
                    getUserProfile(presentation.userId),
                    getPresentationSlidesMetadata(presentation.id)
                ]);
                setCreatedByName(profile.displayName || profile.email || t("Unknown"));
                setSlidesMetadata(slidesMetadata);
            } catch (err) {
                logger.error(err, "[PresentationTemplateItem] Error fetching created by name", { id: presentation.id });
            }
        })();
    }, [presentation.userId, presentation.id]);

    const handleClick = () => {
        onSelect?.(presentation.id, false);
    };

    const handleDoubleClick = () => {
        onSelect?.(presentation.id, true);
    };

    const handleDeletePresentationTemplate = async item => {
        if (await ShowConfirmationDialog({
            title: t("Are you sure you want to delete this template?"),
            message: t("This action can not be undone.")
        })) {
            try {
                await WorkspaceController.deleteWorkspaceResource({
                    resourceId: resource.resourceId,
                    resourceType: resource.resourceType,
                });
            } catch (err) {
                logger.error(err, "[PresentationTemplateItem] Error deleting template", { id: presentation.id });

                ShowErrorDialog({
                    title: t("Unable to delete template"),
                    message: err.message || t("An error occurred while deleting the template. Please try again.")
                });
            }
        }
    };

    const handleOpenPresentationEditor = () => {
        AppController.showEditor({ presentationId: resource.resourceId });
    };

    const handleUpdateTemplateSettings = async (updatedSettings: { description?: string, isPublished?: boolean, name?: string, tags?: string[] }) => {
        if (_.isEmpty(updatedSettings)) {
            return;
        }

        await withProgressDialog({
            title: t("Updating template settings..."),
            action: async () => {
                try {
                    const metadata = _.pickBy({
                        description: updatedSettings.description,
                        isPublished: updatedSettings.isPublished,
                        name: updatedSettings.name
                    }, value => value !== undefined);

                    const tags = updatedSettings.tags;

                    const updates = {
                        resourceId: resource.resourceId,
                        resourceType: resource.resourceType,
                        ...(!_.isEmpty(metadata) && { metadata }),
                        ...(tags && { tags })
                    };

                    await WorkspaceController.updateWorkspaceResource(updates);
                } catch (err) {
                    logger.error(err, "[PresentationTemplateItem] Error updating template settings", { id: presentation.id });

                    ShowErrorDialog({
                        title: t("Unable to edit template settings"),
                        message: err.message
                    });
                }
            }
        });
    };

    const handleEditTemplateSettings = () => {
        ShowDialog(TeamTemplateSettingsDialog, {
            item: {
                name: resource.metadata.name,
                description: resource.metadata.description,
                createdAt: resource.createdAt,
                modifiedAt: presentation.modifiedAt,
                tags: resource.tags,
                createdById: presentation.userId,
                modifiedById: presentation.modifiedBy,
                ratedByCount: resource.metadata.ratedByUsers.length,
                isPublished: resource.metadata.isPublished,
                sips: slideIds,
            },
            existingTags: tags.map(tag => tag.tag),
            editTemplate: handleOpenPresentationEditor,
            updateTemplate: handleUpdateTemplateSettings
        });
    };

    const handlePublish = async () => {
        if (await ShowConfirmationDialog({
            title: t("Are you sure you want to publish this Presentation Template to your team?"),
            message: t("Your team members will be able to create a new presentation starting from this template by selecting it in Create New Presentation dialog.")
        })) {
            try {
                await WorkspaceController.updateWorkspaceResource({
                    resourceId: resource.resourceId,
                    resourceType: resource.resourceType,
                    metadata: {
                        isPublished: true
                    }
                });
            } catch (err) {
                logger.error(err, "[PresentationTemplateItem] Error publishing template", { id: presentation.id });

                ShowErrorDialog({
                    title: t("Unable to update template"),
                    message: err.message
                });
            }
        }
    };

    const handleUnpublish = async () => {
        try {
            await WorkspaceController.updateWorkspaceResource({
                resourceId: resource.resourceId,
                resourceType: resource.resourceType,
                metadata: {
                    isPublished: false
                }
            });
        } catch (err) {
            logger.error(err, "[PresentationTemplateItem] Error unpublishing template", { id: presentation.id });

            ShowErrorDialog({
                title: t("Unable to update template"),
                message: err.message
            });
        }
    };

    const handleToggleLike = async () => {
        const { ratedByUsers } = resource.metadata;

        const metadata: { ratedByUsers?: { add?: string[], remove?: string[] } } = {};
        if (ratedByUsers.includes(firebaseUser.uid)) {
            metadata.ratedByUsers = { remove: [firebaseUser.uid] };
        } else {
            metadata.ratedByUsers = { add: [firebaseUser.uid] };
        }

        try {
            await WorkspaceController.updateWorkspaceResource({
                resourceId: resource.resourceId,
                resourceType: resource.resourceType,
                metadata
            });
        } catch (err) {
            logger.error(err, "[PresentationTemplateItem] Error toggling like", { id: presentation.id });

            ShowErrorDialog({
                title: t("Unable to update template"),
                message: err.message
            });
        }
    };

    const canManageSharedResources = WorkspaceController.actionPermissions[ActionPermissionsObject.SHARED_RESOURCES].manage;
    const canUpgradeToManageSharedResources = WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.SHARED_RESOURCES, PermissionActionType.MANAGE);

    const isPublished = resource.metadata.isPublished;
    const description = resource.metadata.description;
    const name = resource.metadata.name;

    const ratedByCount = resource.metadata.ratedByUsers.length;
    const likedByUser = resource.metadata.ratedByUsers.includes(firebaseUser.uid);
    const favoriteTooltip = likedByUser
        ? t("Click to unfavorite this template.")
        : t("Click to favorite this template.");

    return (<StyledTemplateItem
        key={presentation.id}
        className={selected ? "selected" : ""}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
        ref={containerRef}
    >
        <FlexBox left center fillHeight>
            <div className="title">{name}</div>
            <Gap20 />
            {editable && isPublished && <PublishLabel published>{t("Published")}</PublishLabel>}
            {editable && !isPublished && <PublishLabel onClick={handlePublish}>{t("Draft")}</PublishLabel>}
            <FlexSpacer />
            <FlexBox center onDoubleClick={e => e.stopPropagation()}>
                <Tooltip
                    title={(
                        <div style={{ padding: 10 }}>
                            {favoriteTooltip}
                        </div>
                    )}
                    classes={{ popper: "popper-white rounded-5" }}
                    placement="top"
                    arrow
                    interactive
                >
                    <Stat
                        active={ratedByCount > 0}
                        liked={likedByUser}
                        className="ratedBy"
                        onClick={handleToggleLike}
                    >
                        <Icon>star</Icon>
                        <span>{ratedByCount}</span>
                    </Stat>
                </Tooltip>
                <Gap5 />
                <Tooltip
                    title={
                        (<StyledCreatedByInfo>
                            {description && (<>
                                <div>{description}</div>
                                <Gap10 />
                            </>)}
                            <div>{t("Created by")} <strong>{createdByName}</strong></div>
                        </StyledCreatedByInfo>)
                    }
                    classes={{ popper: "popper-white rounded-5" }}
                    placement="top"
                    arrow
                    interactive
                >
                    <Stat active={!!description}>
                        <Icon className="stat">info</Icon>
                    </Stat>
                </Tooltip>
                {
                    editable &&
                    <IconMenu>
                        {canUpgradeToManageSharedResources && [
                            <MenuItem key="settings" disabled divider><UpgradeIcon /><Gap5 />{t("Template Settings...")}</MenuItem>,
                            <MenuItem key="edit" disabled divider><UpgradeIcon /><Gap5 />{t("Edit Template...")}</MenuItem>,
                            isPublished && <MenuItem key="unpublish" disabled divider><UpgradeIcon /><Gap5 />{t("Unpublish Template")}</MenuItem>,
                            !isPublished && <MenuItem key="publish" disabled divider><UpgradeIcon /><Gap5 />{t("Publish Template")}</MenuItem>,
                        ].filter(Boolean)}
                        {!canUpgradeToManageSharedResources && canManageSharedResources && [
                            <MenuItem key="settings" divider onClick={handleEditTemplateSettings}><Icon>label</Icon>{t("Template Settings...")}</MenuItem>,
                            <MenuItem key="edit" divider onClick={handleOpenPresentationEditor}><Icon>edit</Icon>{t("Edit Template...")}</MenuItem>,
                            isPublished && <MenuItem key="unpublish" divider onClick={handleUnpublish}><Icon>unpublished</Icon>{t("Unpublish Template")}</MenuItem>,
                            !isPublished && <MenuItem key="publish" divider onClick={handlePublish}><Icon>published_with_changes</Icon>{t("Publish Template")}</MenuItem>,
                        ].filter(Boolean)}
                        <MenuItem key="delete" onClick={handleDeletePresentationTemplate}><Icon>delete</Icon>{t("Delete Template")}</MenuItem>
                    </IconMenu>
                }
            </FlexBox>
        </FlexBox>
        <SlideThumbnailsContainer>
            <SlideThumbnails style={canUpgradeToManageSharedResources ? { opacity: 0.5, cursor: "not-allowed" } : {}}>
                {slideIds.map(slideId => {
                    const modifiedAt = slidesMetadata[slideId]?.modifiedAt;
                    if (!modifiedAt) {
                        return <EmptySlideThumbnail key={slideId} />;
                    }

                    return (<SlideThumbnail
                        key={slideId}
                        slideId={slideId}
                        presentationOrLinkId={presentation.id}
                        modifiedAt={modifiedAt}
                        readOnly
                    />);
                })}
            </SlideThumbnails>
        </SlideThumbnailsContainer>
    </StyledTemplateItem>);
});

const PresentationTemplateItemSkeletonContainer = styled.div`
    width: 100%;
    margin-bottom: 20px;

    >span {
        transform: none;
        height: 208px;
        border-radius: 0;
    }
`;

export function PresentationTemplateItemSkeleton() {
    return (
        <PresentationTemplateItemSkeletonContainer>
            <Skeleton />
        </PresentationTemplateItemSkeletonContainer>
    );
}
